import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Navbar from '../components/Navbar';
import { Plus, Edit2, Trash2, Users, Calendar, CheckCircle, XCircle, X, Building2, TrendingUp, Clock, AlertTriangle, BarChart3, Settings, Mail, Key, Globe, Bell, Copy, History, ClipboardEdit, Trash, Archive, ArchiveRestore, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate, formatTimeRange } from '../utils/dateTime';
import ManualHoursModal from '../components/ManualHoursModal';
import ContactButtons from '../components/ContactButtons';
import BulkEmailModal from '../components/BulkEmailModal';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [opportunities, setOpportunities] = useState([]);
  const [signups, setSignups] = useState([]);
  const [pendingSignups, setPendingSignups] = useState([]);
  const [hourRequests, setHourRequests] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  
  // Stats state
  const [statsTimeframe, setStatsTimeframe] = useState('30');
  const [stats, setStats] = useState({
    opportunities_posted: 0,
    total_signups: 0,
    hours_completed: 0,
    no_shows: 0,
    no_show_percentage: 0,
    attended_count: 0,
    late_cancel_count: 0,
    top_partners: [],
    participants_behind: []
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Global Settings state
  const [globalSettings, setGlobalSettings] = useState({
    donation_url: '',
    donation_qr_image: '',
    suggested_amounts: [],
    // Email settings
    enable_email_notifications: false,
    sendgrid_configured: false,
    sendgrid_api_key: '',  // Only for setting, never returned
    from_email: '',
    sender_name: '',
    reply_to_email: '',
    app_base_url: '',
    reminder_hours_before: 24,
    // Twilio SMS settings
    twilio_configured: false,
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_from_number: '',
    // Message templates for "Send as me" in View-as-Participant mode
    reminder_template: '',
    reminder_subject: '',
    thankyou_template: '',
    thankyou_subject: '',
    followup_template: '',
    followup_subject: ''
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  // Collapsible "Advanced / Future Integrations" section (SendGrid + Twilio)
  const [showAdvancedIntegrations, setShowAdvancedIntegrations] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    capacity: 10,
    credit_hours: 2,
    host_contact: '',
    town: '',
    categories: [],
    duration_minutes: 120,
    is_flexible_scheduling: false,
    minimum_age: null,
    accessibility_notes: '',
    what_to_bring: '',
    backup_plan: '',
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_interval: 1,
    recurrence_days: [],
    recurrence_monthly_type: 'day',
    recurrence_monthly_day: 1,
    recurrence_monthly_week: 1,
    recurrence_monthly_weekday: 0,
    recurrence_end_type: 'count',
    recurrence_end_count: 10,
    recurrence_end_date: '',
    repeat_schedule: 'none'
  });

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats?timeframe=${statsTimeframe}`);
      setStats(response.data || {
        opportunities_posted: 0,
        total_signups: 0,
        hours_completed: 0,
        no_shows: 0,
        no_show_percentage: 0,
        attended_count: 0,
        late_cancel_count: 0,
        top_partners: [],
        participants_behind: []
      });
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setStatsLoading(false);
    }
  }, [statsTimeframe]);

  const [opportunityView, setOpportunityView] = useState('upcoming'); // upcoming | past | archived
  const [showBulkEmail, setShowBulkEmail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (tab === 'opportunities') {
        const response = await axios.get(`${API}/opportunities?view=${opportunityView}`);
        setOpportunities(response.data);
      } else if (tab === 'pending') {
        const response = await axios.get(`${API}/signups/pending`);
        setPendingSignups(response.data);
      } else if (tab === 'signups') {
        const response = await axios.get(`${API}/admin/signups`);
        setSignups(response.data);
      } else if (tab === 'participants') {
        const response = await axios.get(`${API}/admin/participants`);
        setParticipants(response.data);
      } else if (tab === 'hours') {
        const [reqRes, manualRes] = await Promise.all([
          axios.get(`${API}/admin/hours`),
          axios.get(`${API}/admin/hours/manual`).catch(() => ({ data: [] })),
        ]);
        setHourRequests(reqRes.data);
        setManualEntries(manualRes.data || []);
      } else if (tab === 'settings') {
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tab, opportunityView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (tab === 'stats') {
      fetchStats();
    }
  }, [tab, fetchStats]);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setGlobalSettings({
        donation_url: response.data.donation_url || '',
        donation_qr_image: response.data.donation_qr_image || '',
        suggested_amounts: response.data.suggested_amounts || [],
        // Email settings
        enable_email_notifications: response.data.enable_email_notifications || false,
        sendgrid_configured: response.data.sendgrid_configured || false,
        sendgrid_api_key: '',  // Never returned from API, only for setting
        from_email: response.data.from_email || '',
        reply_to_email: response.data.reply_to_email || '',
        app_base_url: response.data.app_base_url || '',
        reminder_hours_before: response.data.reminder_hours_before || 24,
        // Twilio (preserve configured flag, tokens are write-only)
        twilio_configured: response.data.twilio_configured || false,
        twilio_account_sid: response.data.twilio_account_sid || '',
        twilio_auth_token: '',
        twilio_from_number: response.data.twilio_from_number || '',
        // Message templates
        reminder_template: response.data.reminder_template || '',
        reminder_subject: response.data.reminder_subject || '',
        thankyou_template: response.data.thankyou_template || '',
        thankyou_subject: response.data.thankyou_subject || '',
        followup_template: response.data.followup_template || '',
        followup_subject: response.data.followup_subject || ''
      });
    } catch (err) {
      console.error('fetchSettings failed', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {    try {
      const updateData = {
        donation_url: globalSettings.donation_url || null,
        donation_qr_image: globalSettings.donation_qr_image || null,
        suggested_amounts: globalSettings.suggested_amounts,
        // Email settings
        enable_email_notifications: globalSettings.enable_email_notifications,
        from_email: globalSettings.from_email || null,
        sender_name: globalSettings.sender_name || null,
        reply_to_email: globalSettings.reply_to_email || null,
        app_base_url: globalSettings.app_base_url || null,
        reminder_hours_before: globalSettings.reminder_hours_before || 24,
        // Twilio settings
        twilio_account_sid: globalSettings.twilio_account_sid || null,
        twilio_from_number: globalSettings.twilio_from_number || null,
        // Message templates (send empty strings as null so the backend can fall back to built-in defaults)
        reminder_template: globalSettings.reminder_template || null,
        reminder_subject: globalSettings.reminder_subject || null,
        thankyou_template: globalSettings.thankyou_template || null,
        thankyou_subject: globalSettings.thankyou_subject || null,
        followup_template: globalSettings.followup_template || null,
        followup_subject: globalSettings.followup_subject || null,
      };

      // Only include API keys if user entered new ones
      if (globalSettings.sendgrid_api_key) {
        updateData.sendgrid_api_key = globalSettings.sendgrid_api_key;
      }
      if (globalSettings.twilio_auth_token) {
        updateData.twilio_auth_token = globalSettings.twilio_auth_token;
      }
      
      await axios.put(`${API}/admin/settings`, updateData);
      toast.success('Settings saved');
      // Refresh settings to get updated sendgrid_configured status
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(day)
        ? prev.recurrence_days.filter(d => d !== day)
        : [...prev.recurrence_days, day]
    }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOpp) {
        // If editing a recurring opportunity, ask for edit mode
        let updateData = { ...formData };
        if (editingOpp.series_id) {
          const editMode = window.confirm(
            'This is part of a recurring series.\n\nClick OK to edit the entire series, or Cancel to edit only this occurrence.'
          ) ? 'series' : 'single';
          updateData.edit_mode = editMode;
        }
        
        try {
          await axios.put(`${API}/opportunities/${editingOpp.id}`, updateData);
          toast.success('Opportunity updated');
        } catch (err) {
          // Phase 5: capacity-conflict — offer override
          const detail = err.response?.data?.detail || '';
          if (err.response?.status === 400 && detail.includes('Cannot lower capacity')) {
            if (window.confirm(`${detail}\n\nDo you want to proceed anyway and keep all current signups?`)) {
              await axios.put(`${API}/opportunities/${editingOpp.id}`, { ...updateData, allow_overcapacity: true });
              toast.success('Opportunity updated (capacity override applied)');
            } else {
              return;
            }
          } else {
            throw err;
          }
        }
      } else {
        await axios.post(`${API}/opportunities`, formData);
        toast.success(formData.repeat_schedule !== 'none' ? 'Recurring opportunities created' : 'Opportunity created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save opportunity');
    }
  };

  // Phase 1: Duplicate Opportunity
  const handleDuplicate = async (opp) => {
    if (!window.confirm(`Duplicate "${opp.title}"?\n\nThis creates a new, empty opportunity with the same details — no signups, attendance, or hours are copied.`)) return;
    try {
      const res = await axios.post(`${API}/opportunities/${opp.id}/duplicate`);
      toast.success(`Duplicated as "${res.data.title}"`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate opportunity');
    }
  };

  // Archive / Unarchive — preserves all signups, attendance, hours, history.
  const handleArchive = async (opp) => {
    const action = opp.is_archived ? 'unarchive' : 'archive';
    const verb = opp.is_archived ? 'restore' : 'archive';
    if (!window.confirm(`${verb === 'archive' ? 'Archive' : 'Restore'} "${opp.title}"?\n\nThis is a UI-only change — all signups, attendance, hours and history are preserved.`)) return;
    try {
      await axios.put(`${API}/opportunities/${opp.id}/${action}`);
      toast.success(opp.is_archived ? 'Opportunity restored' : 'Opportunity archived');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb}`);
    }
  };

  // Phase 3: View edit history
  const [historyOpp, setHistoryOpp] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const handleViewHistory = async (opp) => {
    setHistoryOpp(opp);
    setHistoryLoading(true);
    setHistoryEntries([]);
    try {
      const res = await axios.get(`${API}/opportunities/${opp.id}/history`);
      setHistoryEntries(res.data.history || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Manual Hours Entry
  const [showManualHours, setShowManualHours] = useState(false);
  const [manualEntries, setManualEntries] = useState([]);
  const fetchManualEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/hours/manual`);
      setManualEntries(res.data || []);
    } catch (err) {
      // ignore
    }
  }, []);
  const handleDeleteManualEntry = async (entry) => {
    if (!window.confirm(`Delete this manual entry of ${entry.credit_hours}h for ${entry.user_name}?\n\nThe participant's total hours will be reduced by ${entry.credit_hours}.`)) return;
    try {
      await axios.delete(`${API}/admin/hours/manual/${entry.id}`);
      toast.success('Manual entry deleted');
      fetchManualEntries();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };
  const handleDelete = async (opp) => {
    let confirmMessage = 'Are you sure you want to delete this opportunity?';
    let deleteMode = 'single';
    
    if (opp.series_id) {
      const deleteSeries = window.confirm(
        'This is part of a recurring series.\n\nClick OK to delete the entire series, or Cancel to delete only this occurrence.'
      );
      deleteMode = deleteSeries ? 'series' : 'single';
      
      if (deleteMode === 'series') {
        confirmMessage = 'Are you sure you want to delete ALL occurrences in this series?';
      }
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      await axios.delete(`${API}/opportunities/${opp.id}?delete_mode=${deleteMode}`);
      toast.success(deleteMode === 'series' ? 'Series deleted' : 'Opportunity deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete opportunity');
    }
  };

  const handleEdit = (opp) => {
    setEditingOpp(opp);
    setFormData({
      title: opp.title,
      date: opp.date,
      start_time: opp.start_time,
      end_time: opp.end_time,
      location: opp.location,
      description: opp.description,
      capacity: opp.capacity,
      credit_hours: opp.credit_hours,
      host_contact: opp.host_contact,
      town: opp.town || '',
      categories: opp.categories || [],
      duration_minutes: opp.duration_minutes || 120,
      is_flexible_scheduling: opp.is_flexible_scheduling || false,
      minimum_age: opp.minimum_age || null,
      accessibility_notes: opp.accessibility_notes || '',
      what_to_bring: opp.what_to_bring || '',
      backup_plan: opp.backup_plan || '',
      is_recurring: false,
      recurrence_type: 'weekly',
      recurrence_interval: 1,
      recurrence_days: [],
      recurrence_monthly_type: 'day',
      recurrence_monthly_day: 1,
      recurrence_monthly_week: 1,
      recurrence_monthly_weekday: 0,
      recurrence_end_type: 'count',
      recurrence_end_count: 10,
      recurrence_end_date: '',
      repeat_schedule: opp.repeat_schedule || 'none'
    });
    setShowModal(true);
  };

  const handleApproveHours = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/hours/${requestId}`, { status });
      toast.success(`Hours ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update hours');
    }
  };

  const handleApproveSignup = async (signupId, status) => {
    try {
      const data = { status };
      if (status === 'declined' && declineReason) {
        data.declined_reason = declineReason;
      }
      await axios.put(`${API}/signups/${signupId}/approve`, data);
      toast.success(`Signup ${status}`);
      setDeclineReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update signup');
    }
  };

  const handleDeleteParticipant = async (participant) => {
    if (!window.confirm(`Delete ${participant.name} (${participant.email})?\n\nThis will permanently remove their account and all related data (signups, time entries, hours, etc.).\n\nThis cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/participants/${participant.id}`);
      toast.success(res.data.message || 'Participant deleted');
      setParticipants(prev => prev.filter(p => p.id !== participant.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete participant');
    }
  };

  const viewParticipantDetail = async (participantId) => {
    try {
      const response = await axios.get(`${API}/admin/participants/${participantId}`);
      setSelectedParticipant(response.data);
      setShowParticipantModal(true);
    } catch (error) {
      toast.error('Failed to load participant details');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      description: '',
      capacity: 10,
      credit_hours: 2,
      host_contact: '',
      town: '',
      categories: [],
      duration_minutes: 120,
      is_flexible_scheduling: false,
      minimum_age: null,
      accessibility_notes: '',
      what_to_bring: '',
      backup_plan: '',
      is_recurring: false,
      recurrence_type: 'weekly',
      recurrence_interval: 1,
      recurrence_days: [],
      recurrence_monthly_type: 'day',
      recurrence_monthly_day: 1,
      recurrence_monthly_week: 1,
      recurrence_monthly_weekday: 0,
      recurrence_end_type: 'count',
      recurrence_end_count: 10,
      recurrence_end_date: '',
      repeat_schedule: 'none'
    });
    setEditingOpp(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">Admin Dashboard</h1>
          <p className="text-base text-[#666666]">Manage opportunities, signups, and hour approvals.</p>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
              tab === 'stats'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('stats')}
            data-testid="stats-tab"
          >
            <BarChart3 className="w-4 h-4" />
            Overview
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === 'opportunities'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('opportunities')}
            data-testid="opportunities-tab"
          >
            Opportunities
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === 'pending'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('pending')}
            data-testid="pending-approvals-tab"
          >
            Pending Approvals
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === 'participants'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('participants')}
            data-testid="participants-tab"
          >
            Participants
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === 'signups'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('signups')}
            data-testid="signups-tab"
          >
            All Sign-ups
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === 'hours'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('hours')}
            data-testid="hours-tab"
          >
            Hour Approvals
          </button>
          <Link
            to="/admin/partner-organizations"
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C] flex items-center gap-2"
            data-testid="partner-orgs-link"
          >
            <Building2 className="w-4 h-4" />
            Partner Orgs
          </Link>
          <Link
            to="/admin/reports"
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C] flex items-center gap-2"
            data-testid="reports-link"
          >
            <BarChart3 className="w-4 h-4" />
            Reports
          </Link>
          <Link
            to="/admin/email-logs"
            className="px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C] flex items-center gap-2"
            data-testid="email-logs-link"
          >
            <Mail className="w-4 h-4" />
            Email Logs
          </Link>
          <button
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
              tab === 'settings'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setTab('settings')}
            data-testid="settings-tab"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>

        {tab === 'stats' && (
          <div data-testid="stats-content">
            {/* Timeframe Selector */}
            <div className="mb-6">
              <div className="flex gap-2">
                <button
                  onClick={() => setStatsTimeframe('7')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    statsTimeframe === '7' ? 'bg-[#C9A227] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
                  }`}
                  data-testid="timeframe-7"
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => setStatsTimeframe('30')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    statsTimeframe === '30' ? 'bg-[#C9A227] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
                  }`}
                  data-testid="timeframe-30"
                >
                  Last 30 Days
                </button>
                <button
                  onClick={() => setStatsTimeframe('month')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    statsTimeframe === 'month' ? 'bg-[#C9A227] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
                  }`}
                  data-testid="timeframe-month"
                >
                  This Month
                </button>
              </div>
            </div>

            {statsLoading ? (
              <div className="text-center py-12">Loading stats...</div>
            ) : (
              <>
                {/* Main Stats Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {/* Opportunities Posted */}
                  <div className="card" data-testid="stat-opportunities">
                    <div className="flex items-start justify-between mb-3">
                      <Calendar className="w-8 h-8 text-[#8B2E3C]" />
                    </div>
                    <div className="text-3xl font-bold text-[#222222] mb-1">{stats.opportunities_posted || 0}</div>
                    <div className="text-sm text-[#666666] mb-3">Opportunities Posted</div>
                    <button
                      onClick={() => setTab('opportunities')}
                      className="text-[#8B2E3C] text-sm hover:underline"
                    >
                      View details →
                    </button>
                  </div>

                  {/* Total Signups */}
                  <div className="card" data-testid="stat-signups">
                    <div className="flex items-start justify-between mb-3">
                      <Users className="w-8 h-8 text-[#C9A227]" />
                    </div>
                    <div className="text-3xl font-bold text-[#222222] mb-1">{stats.total_signups || 0}</div>
                    <div className="text-sm text-[#666666] mb-3">Total Signups</div>
                    <button
                      onClick={() => setTab('signups')}
                      className="text-[#8B2E3C] text-sm hover:underline"
                    >
                      View details →
                    </button>
                  </div>

                  {/* Hours Completed */}
                  <div className="card" data-testid="stat-hours">
                    <div className="flex items-start justify-between mb-3">
                      <Clock className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="text-3xl font-bold text-[#222222] mb-1">{(stats.hours_completed || 0).toFixed(1)}h</div>
                    <div className="text-sm text-[#666666] mb-3">Hours Completed</div>
                    <div className="text-xs text-[#666666]">{stats.attended_count || 0} volunteers attended</div>
                  </div>

                  {/* No-Shows */}
                  <div className="card" data-testid="stat-noshows">
                    <div className="flex items-start justify-between mb-3">
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <div className="text-3xl font-bold text-[#222222] mb-1">
                      {stats.no_shows || 0}
                      <span className="text-lg ml-2 text-red-600">({stats.no_show_percentage || 0}%)</span>
                    </div>
                    <div className="text-sm text-[#666666] mb-3">No-Shows</div>
                    <div className="text-xs text-[#666666]">{stats.late_cancel_count || 0} late cancels</div>
                  </div>
                </div>

                {/* Top Partners */}
                {(stats.top_partners || []).length > 0 && (
                  <div className="card mb-6" data-testid="stat-top-partners">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-[#222222]">Top Partners by Hours</h3>
                      <Link to="/admin/partner-organizations" className="text-[#8B2E3C] text-sm hover:underline">
                        View all partners →
                      </Link>
                    </div>
                    <div className="space-y-3">
                      {(stats.top_partners || []).map((partner, index) => (
                        <div key={partner.id || index} className="flex items-center justify-between bg-[#FBF4EA] rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#8B2E3C] rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {index + 1}
                            </div>
                            <div className="font-medium text-[#222222]">{partner.org_name || 'Unknown'}</div>
                          </div>
                          <div className="font-bold text-[#8B2E3C]">{(partner.hours || 0).toFixed(1)}h</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Participants Behind (only show if there are any) */}
                {(stats.participants_behind || []).length > 0 && (
                  <div className="card" data-testid="stat-behind">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-[#222222]">Participants Behind on Hours</h3>
                      <button
                        onClick={() => setTab('participants')}
                        className="text-[#8B2E3C] text-sm hover:underline"
                      >
                        View all participants →
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(stats.participants_behind || []).map((p, index) => (
                        <div key={p.id || index} className="flex items-center justify-between bg-red-50 rounded-lg p-3">
                          <div className="font-medium text-[#222222]">{p.name || 'Unknown'}</div>
                          <div className="text-sm text-red-600">
                            {p.completed_hours || 0}h / {p.required_hours || 0}h required
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Summary */}
                <div className="mt-6 p-4 bg-[#FBF4EA] rounded-lg border-2 border-[#E9DDCF]">
                  <div className="text-sm text-[#666666]">
                    <TrendingUp className="w-4 h-4 inline mr-2" />
                    Summary: <strong>{stats.opportunities_posted || 0}</strong> opportunities created, <strong>{stats.total_signups || 0}</strong> signups received, <strong>{(stats.hours_completed || 0).toFixed(1)}</strong> volunteer hours logged in this period.
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'opportunities' && (
          <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <button
                onClick={openCreateModal}
                className="btn-primary flex items-center gap-2"
                data-testid="create-opportunity-button"
              >
                <Plus className="w-5 h-5" />
                Create Opportunity
              </button>
              <div className="flex bg-white rounded-lg border border-[#E9DDCF] overflow-hidden self-start sm:self-auto">
                {[
                  { key: 'upcoming', label: 'Upcoming' },
                  { key: 'past', label: 'Past' },
                  { key: 'archived', label: 'Archived' },
                ].map(v => (
                  <button
                    key={v.key}
                    onClick={() => setOpportunityView(v.key)}
                    className={`px-4 py-2 text-sm font-medium ${opportunityView === v.key ? 'bg-[#8B2E3C] text-white' : 'text-[#666] hover:bg-[#FBF4EA]'}`}
                    data-testid={`opportunity-view-${v.key}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {opportunities.length === 0 ? (
              <div className="card text-center py-12" data-testid="opportunities-empty">
                <p className="text-[#666666]">
                  {opportunityView === 'upcoming' && 'No upcoming opportunities. Create one above.'}
                  {opportunityView === 'past' && 'No past opportunities yet — completed opportunities will appear here.'}
                  {opportunityView === 'archived' && 'No archived opportunities.'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {opportunities.map((opp) => {
                  const oppDate = parseLocalDate(opp.date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  // Phase 2: "Updated" badge if last edited within last 7 days
                  const isRecentlyEdited = opp.last_edited_at &&
                    (new Date() - new Date(opp.last_edited_at)) < 7 * 24 * 60 * 60 * 1000;
                  return (
                    <div key={opp.id} className="card" data-testid={`opportunity-card-${opp.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-xl font-bold text-[#222222]">{opp.title}</h3>
                            {opp.series_id && (
                              <span className="badge bg-[#C9A227] text-[#222222] text-xs">Recurring</span>
                            )}
                            {isRecentlyEdited && (
                              <span
                                className="badge bg-blue-100 text-blue-800 text-xs font-medium"
                                title={`Last edited ${format(new Date(opp.last_edited_at), 'MMM d, yyyy h:mm a')}${opp.last_edited_by_name ? ` by ${opp.last_edited_by_name}` : ''}`}
                                data-testid={`updated-badge-${opp.id}`}
                              >
                                Updated
                              </span>
                            )}
                            {opp.is_completed && !opp.is_archived && (
                              <span className="badge bg-emerald-100 text-emerald-800 text-xs font-medium" data-testid={`completed-badge-${opp.id}`}>
                                Completed
                              </span>
                            )}
                            {opp.is_archived && (
                              <span className="badge bg-gray-200 text-gray-700 text-xs font-medium" data-testid={`archived-badge-${opp.id}`}>
                                Archived
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-[#666666] mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {formattedDate} • {formatTimeRange(opp.start_time, opp.end_time)}
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              {opp.signups_count} / {opp.capacity} signed up
                            </div>
                          </div>
                          <p className="text-sm text-[#666666]">{opp.location}</p>
                          {isRecentlyEdited && opp.last_changed_fields?.length > 0 && (
                            <p className="text-xs text-blue-700 mt-1.5" data-testid={`changed-fields-${opp.id}`}>
                              Recently changed: {opp.last_changed_fields.join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(opp)}
                            className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                            title="Edit"
                            data-testid={`edit-button-${opp.id}`}
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(opp)}
                            className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                            title="Duplicate"
                            data-testid={`duplicate-button-${opp.id}`}
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleViewHistory(opp)}
                            className="p-2 text-[#666] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                            title="Edit history"
                            data-testid={`history-button-${opp.id}`}
                          >
                            <History className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleArchive(opp)}
                            className="p-2 text-[#666] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                            title={opp.is_archived ? 'Restore (unarchive)' : 'Archive'}
                            data-testid={`archive-button-${opp.id}`}
                          >
                            {opp.is_archived
                              ? <ArchiveRestore className="w-5 h-5" />
                              : <Archive className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleDelete(opp)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                            data-testid={`delete-button-${opp.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'pending' && (
          <div>
            {pendingSignups.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-[#666666]">No pending signup requests.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {pendingSignups.map((signup) => {
                  const oppDate = parseLocalDate(signup.opportunity_date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  
                  return (
                    <div key={signup.id} className="card" data-testid={`pending-signup-${signup.id}`}>
                      <div className="flex flex-col gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-[#222222] mb-2">{signup.opportunity_title}</h3>
                          <p className="text-sm text-[#666666] mb-2">
                            <strong>{signup.user_name}</strong> ({signup.user_email})
                          </p>
                          <div className="flex items-center gap-2 text-sm text-[#666666]">
                            <Calendar className="w-4 h-4" />
                            {formattedDate}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveSignup(signup.id, 'approved')}
                            className="btn-primary flex items-center gap-2 text-sm"
                            data-testid={`approve-signup-button-${signup.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const reason = window.prompt('Optional: Reason for declining?');
                              if (reason !== null) {
                                setDeclineReason(reason);
                                handleApproveSignup(signup.id, 'declined');
                              }
                            }}
                            className="btn-outline flex items-center gap-2 text-sm border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                            data-testid={`decline-signup-button-${signup.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'participants' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Program Participants ({participants.length})</h2>
              <button
                onClick={() => setShowBulkEmail(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="admin-bulk-email-button"
              >
                <Send className="w-4 h-4" />
                Bulk Email
              </button>
            </div>

            {participants.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-[#666666]">No participants yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[#E9DDCF]">
                      <th className="text-left py-3 px-4 font-semibold">Name</th>
                      <th className="text-left py-3 px-4 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 font-semibold">Status</th>
                      <th className="text-right py-3 px-4 font-semibold">Signed-up Hours</th>
                      <th className="text-right py-3 px-4 font-semibold">Completed Hours</th>
                      <th className="text-right py-3 px-4 font-semibold">Total Signups</th>
                      <th className="text-left py-3 px-4 font-semibold">Last Activity</th>
                      <th className="text-center py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant) => (
                      <tr key={participant.id} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]" data-testid={`participant-row-${participant.id}`}>
                        <td className="py-3 px-4 font-medium">{participant.name}</td>
                        <td className="py-3 px-4 text-sm text-[#666666]">{participant.email}</td>
                        <td className="py-3 px-4">
                          <span className={`badge ${participant.status === 'Active' ? 'badge-approved' : 'badge-pending'}`}>
                            {participant.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-[#C9A227]">
                          {participant.signed_up_hours.toFixed(1)}h
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-[#8B2E3C]">
                          {participant.completed_hours.toFixed(1)}h
                        </td>
                        <td className="py-3 px-4 text-right">{participant.total_signups}</td>
                        <td className="py-3 px-4 text-sm text-[#666666]">
                          {participant.last_activity ? format(new Date(participant.last_activity), 'MMM d, yyyy') : 'Never'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ContactButtons
                              email={participant.email}
                              phone={participant.phone}
                              name={participant.name}
                              compact
                            />
                            <button
                              onClick={() => navigate(`/admin/participants/${participant.id}`)}
                              className="text-[#8B2E3C] hover:underline text-sm font-medium"
                              data-testid={`view-participant-${participant.id}`}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteParticipant(participant)}
                              className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                              title="Delete participant"
                              data-testid={`delete-participant-${participant.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'signups' && (
          <div>
            {signups.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-[#666666]">No signups yet.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {signups.map((signup) => {
                  const oppDate = parseLocalDate(signup.opportunity_date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  
                  return (
                    <div key={signup.id} className="card" data-testid={`signup-item-${signup.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-bold text-[#222222]">{signup.opportunity_title}</h3>
                          <p className="text-sm text-[#666666] mt-1">
                            {signup.user_name} ({signup.user_email})
                          </p>
                          <div className="flex items-center gap-2 text-sm text-[#666666] mt-2">
                            <Calendar className="w-4 h-4" />
                            {formattedDate}
                          </div>
                        </div>
                        <div>
                          {signup.status === 'pending' && (
                            <span className="badge badge-pending">Pending</span>
                          )}
                          {signup.status === 'approved' && (
                            <span className="badge badge-approved">Approved</span>
                          )}
                          {signup.status === 'declined' && (
                            <span className="badge" style={{backgroundColor: '#FEE2E2', color: '#991B1B'}}>Declined</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'hours' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#666]">Approve volunteer-submitted hours, or add manual hours when participants miss check-in/check-out.</p>
              <button
                onClick={() => setShowManualHours(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="add-manual-hours-button"
              >
                <ClipboardEdit className="w-4 h-4" />
                Add Manual Hours
              </button>
            </div>

            {manualEntries.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#222] mb-3">Manual Entries ({manualEntries.length})</h3>
                <div className="grid gap-3" data-testid="manual-entries-list">
                  {manualEntries.map((m) => {
                    const dt = parseLocalDate(m.opportunity_date);
                    const dateStr = format(dt, 'MMM d, yyyy');
                    return (
                      <div key={m.id} className="card border-l-4 border-l-blue-500" data-testid={`manual-entry-${m.id}`}>
                        <div className="flex flex-col sm:flex-row justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="font-bold text-[#222]">{m.opportunity_title}</h4>
                              <span className="badge bg-blue-100 text-blue-800 text-xs" data-testid="manual-entry-badge">Manual Entry</span>
                              {m.partner_org_name && (
                                <span className="badge bg-[#FBF4EA] text-[#8B2E3C] text-xs">{m.partner_org_name}</span>
                              )}
                            </div>
                            <p className="text-sm text-[#666]">{m.user_name} ({m.user_email})</p>
                            <div className="text-sm text-[#666] mt-1 flex flex-wrap gap-4">
                              <span><Calendar className="inline w-3.5 h-3.5 mr-1" />{dateStr}</span>
                              <span><strong>{m.credit_hours}</strong> hours credited</span>
                            </div>
                            <p className="text-xs text-[#888] mt-1.5 italic">Reason: {m.reason}</p>
                            <p className="text-xs text-[#888] mt-0.5">
                              Entered by {m.entered_by_name || m.entered_by_email} ({m.entered_by_role}) on {m.entered_at ? format(new Date(m.entered_at), 'MMM d, yyyy h:mm a') : ''}
                            </p>
                            {m.edit_history && m.edit_history.length > 0 && (
                              <p className="text-xs text-blue-700 mt-0.5" data-testid={`manual-entry-edits-${m.id}`}>
                                {m.edit_history.length} edit(s) recorded
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteManualEntry(m)}
                            className="self-start p-2 text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                            title="Delete manual entry"
                            data-testid={`delete-manual-entry-${m.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h3 className="text-lg font-bold text-[#222] mb-3">Volunteer Hour Requests</h3>
            {hourRequests.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-[#666666]">No pending hour requests.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {hourRequests.map((request) => {
                  const oppDate = parseLocalDate(request.opportunity_date);
                  const formattedDate = format(oppDate, 'MMM d, yyyy');
                  const isPending = request.status === 'pending';
                  
                  return (
                    <div key={request.id} className="card" data-testid={`hour-request-${request.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-bold text-[#222222]">{request.opportunity_title}</h3>
                            <span className={`badge ${isPending ? 'badge-pending' : 'badge-approved'}`}>
                              {request.status}
                            </span>
                          </div>
                          <p className="text-sm text-[#666666] mb-2">
                            {request.user_name} ({request.user_email})
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm text-[#666666]">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {formattedDate}
                            </div>
                            <div>
                              <strong>{request.credit_hours}</strong> hours requested
                            </div>
                          </div>
                        </div>
                        {isPending && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveHours(request.id, 'approved')}
                              className="btn-primary flex items-center gap-2 text-sm"
                              data-testid={`approve-button-${request.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleApproveHours(request.id, 'rejected')}
                              className="btn-outline flex items-center gap-2 text-sm border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                              data-testid={`reject-button-${request.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="opportunity-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#222222] mb-6">
                {editingOpp ? 'Edit Opportunity' : 'Create Opportunity'}
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Title *</label>
                    <input
                      type="text"
                      name="title"
                      className="input-field"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      data-testid="modal-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Date *</label>
                    <input
                      type="date"
                      name="date"
                      className="input-field"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      data-testid="modal-date-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Time *</label>
                      <input
                        type="time"
                        name="start_time"
                        className="input-field"
                        value={formData.start_time}
                        onChange={handleInputChange}
                        required
                        data-testid="modal-start-time-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Time *</label>
                      <input
                        type="time"
                        name="end_time"
                        className="input-field"
                        value={formData.end_time}
                        onChange={handleInputChange}
                        required
                        data-testid="modal-end-time-input"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Location *</label>
                    <input
                      type="text"
                      name="location"
                      className="input-field"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., 123 Main St, Ossipee, NH"
                      data-testid="modal-location-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Capacity *</label>
                    <input
                      type="number"
                      name="capacity"
                      className="input-field"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      required
                      min="1"
                      data-testid="modal-capacity-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Credit Hours *</label>
                    <input
                      type="number"
                      name="credit_hours"
                      className="input-field"
                      value={formData.credit_hours}
                      onChange={handleInputChange}
                      required
                      min="0.5"
                      step="0.5"
                      data-testid="modal-credit-hours-input"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Host Contact *</label>
                    <input
                      type="text"
                      name="host_contact"
                      className="input-field"
                      value={formData.host_contact}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., John Doe - john@example.com"
                      data-testid="modal-host-contact-input"
                    />
                  </div>

                  <div className="sm:col-span-2 border-t border-[#E9DDCF] pt-4">
                    <label className="block text-sm font-medium mb-2">Repeat Schedule</label>
                    <select
                      name="repeat_schedule"
                      className="input-field"
                      value={formData.repeat_schedule}
                      onChange={handleInputChange}
                      data-testid="modal-repeat-schedule"
                    >
                      <option value="none">No (One-time)</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {formData.repeat_schedule !== 'none' && (
                      <p className="text-xs text-[#666] mt-1">
                        Up to 12 occurrences will be generated automatically.
                        {editingOpp?.series_id && ' Editing the series will regenerate future occurrences.'}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <textarea
                      name="description"
                      className="input-field"
                      rows="4"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe what volunteers will do..."
                      data-testid="modal-description-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Town</label>
                    <input
                      type="text"
                      name="town"
                      className="input-field"
                      value={formData.town}
                      onChange={handleInputChange}
                      placeholder="e.g., Ossipee"
                      data-testid="modal-town-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      name="duration_minutes"
                      className="input-field"
                      value={formData.duration_minutes}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="e.g., 120"
                      data-testid="modal-duration-input"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-2">Categories</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {['Serving Food', 'Cleanup', 'Childcare', 'Admin', 'Outdoor Labor', 'Other'].map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleCategoryToggle(cat)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            (formData.categories || []).includes(cat)
                              ? 'bg-[#8B2E3C] text-white'
                              : 'bg-[#E9DDCF] text-[#222222] hover:bg-[#C9A227]'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_flexible_scheduling"
                        name="is_flexible_scheduling"
                        checked={formData.is_flexible_scheduling}
                        onChange={handleInputChange}
                        className="w-4 h-4"
                      />
                      <label htmlFor="is_flexible_scheduling" className="text-sm font-medium">
                        Flexible Scheduling
                      </label>
                    </div>
                  </div>
                </div>

                {editingOpp && (
                  <p className="text-xs text-[#8B2E3C] bg-[#FBF4EA] border border-[#E9DDCF] rounded-lg px-3 py-2 mb-3" data-testid="notification-banner">
                    Signed-up participants will be notified by email if date, time, location, or details change.
                  </p>
                )}
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-outline"
                    data-testid="modal-cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    data-testid="modal-save-button"
                  >
                    {editingOpp ? 'Update & Notify' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showParticipantModal && selectedParticipant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="participant-modal">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#222222] mb-2">{selectedParticipant.name}</h2>
                  <p className="text-[#666666]">{selectedParticipant.email}</p>
                  <span className={`badge ${selectedParticipant.status === 'Active' ? 'badge-approved' : 'badge-pending'} mt-2`}>
                    {selectedParticipant.status}
                  </span>
                </div>
                <button
                  onClick={() => setShowParticipantModal(false)}
                  className="text-[#666666] hover:text-[#222222]"
                  data-testid="close-participant-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="card bg-[#FFF9F0]">
                  <div className="text-sm text-[#666666] mb-1">Signed-up Hours</div>
                  <div className="text-2xl font-bold text-[#C9A227]">{selectedParticipant.signed_up_hours.toFixed(1)}h</div>
                  <div className="text-xs text-[#666666] mt-1">From approved signups</div>
                </div>
                <div className="card bg-[#FFF0F0]">
                  <div className="text-sm text-[#666666] mb-1">Completed Hours</div>
                  <div className="text-2xl font-bold text-[#8B2E3C]">{selectedParticipant.completed_hours.toFixed(1)}h</div>
                  <div className="text-xs text-[#666666] mt-1">Approved and verified</div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">Sign-ups ({selectedParticipant.signups.length})</h3>
                {selectedParticipant.signups.length === 0 ? (
                  <p className="text-[#666666] text-sm">No signups yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedParticipant.signups.map((signup) => (
                      <div key={signup.id} className="card text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{signup.opportunity_title}</div>
                            <div className="text-[#666666] text-xs mt-1">
                              {format(parseLocalDate(signup.opportunity_date), 'MMM d, yyyy')}
                            </div>
                          </div>
                          <div>
                            {signup.status === 'pending' && (
                              <span className="badge badge-pending">Pending</span>
                            )}
                            {signup.status === 'approved' && (
                              <span className="badge badge-approved">Approved</span>
                            )}
                            {signup.status === 'declined' && (
                              <span className="badge" style={{backgroundColor: '#FEE2E2', color: '#991B1B'}}>Declined</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-bold mb-3">Hour Requests ({selectedParticipant.hour_requests.length})</h3>
                {selectedParticipant.hour_requests.length === 0 ? (
                  <p className="text-[#666666] text-sm">No hour requests yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedParticipant.hour_requests.map((request) => (
                      <div key={request.id} className="card text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{request.opportunity_title}</div>
                            <div className="text-[#666666] text-xs mt-1">
                              {format(parseLocalDate(request.opportunity_date), 'MMM d, yyyy')} • {request.credit_hours}h requested
                            </div>
                          </div>
                          <div>
                            {request.status === 'pending' && (
                              <span className="badge badge-pending">Pending</span>
                            )}
                            {request.status === 'approved' && (
                              <span className="badge badge-approved">Approved</span>
                            )}
                            {request.status === 'rejected' && (
                              <span className="badge" style={{backgroundColor: '#FEE2E2', color: '#991B1B'}}>Rejected</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div data-testid="settings-content">
          <h2 className="text-2xl font-bold text-[#222222] mb-6">Global Settings</h2>
          
          {settingsLoading ? (
            <div className="text-center py-12">Loading settings...</div>
          ) : (
            <div className="space-y-6">
              {/* Donation Settings Card */}
              <div className="card">
                <h3 className="text-lg font-bold text-[#222222] mb-4">Donation Settings</h3>
                <p className="text-sm text-[#666666] mb-4">
                  Configure donation messaging that appears across the platform to help sustain the tool.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Donation URL</label>
                    <input
                      type="url"
                      className="input-field"
                      placeholder="https://donate.example.com/kindness-circle"
                      value={globalSettings.donation_url}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, donation_url: e.target.value }))}
                      data-testid="donation-url-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">Leave empty to show "Donation link coming soon" message</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">QR Code Image URL (optional)</label>
                    <input
                      type="url"
                      className="input-field"
                      placeholder="https://example.com/qr-code.png"
                      value={globalSettings.donation_qr_image}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, donation_qr_image: e.target.value }))}
                      data-testid="donation-qr-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">QR code image for the donation page (shows in modals)</p>
                  </div>
                  
                  {globalSettings.donation_qr_image && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#666666]">Preview:</span>
                      <img 
                        src={globalSettings.donation_qr_image} 
                        alt="QR Preview" 
                        className="w-20 h-20 border-2 border-[#E9DDCF] rounded-lg"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="mt-6 pt-4 border-t border-[#E9DDCF]">
                  <h4 className="font-medium text-[#222222] mb-3">Suggested Donation Amounts</h4>
                  <div className="space-y-2 mb-4">
                    {(globalSettings.suggested_amounts || []).map((item, index) => (
                      <div key={`amt-${item.label || ''}-${index}`} className="flex items-center gap-2 bg-[#FBF4EA] rounded-lg p-2">
                        <input
                          type="text"
                          className="input-field flex-1"
                          placeholder="Label (e.g., Per opportunity)"
                          value={item.label || ''}
                          onChange={(e) => {
                            const newAmounts = [...globalSettings.suggested_amounts];
                            newAmounts[index] = { ...newAmounts[index], label: e.target.value };
                            setGlobalSettings(prev => ({ ...prev, suggested_amounts: newAmounts }));
                          }}
                        />
                        <input
                          type="text"
                          className="input-field w-32"
                          placeholder="Amount"
                          value={item.amount || ''}
                          onChange={(e) => {
                            const newAmounts = [...globalSettings.suggested_amounts];
                            newAmounts[index] = { ...newAmounts[index], amount: e.target.value };
                            setGlobalSettings(prev => ({ ...prev, suggested_amounts: newAmounts }));
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newAmounts = globalSettings.suggested_amounts.filter((_, i) => i !== index);
                            setGlobalSettings(prev => ({ ...prev, suggested_amounts: newAmounts }));
                          }}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newAmounts = [...(globalSettings.suggested_amounts || []), { label: '', amount: '' }];
                      setGlobalSettings(prev => ({ ...prev, suggested_amounts: newAmounts }));
                    }}
                    className="text-[#8B2E3C] text-sm hover:underline"
                  >
                    + Add suggested amount
                  </button>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSaveSettings}
                    className="btn-primary"
                    data-testid="save-settings-button"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
              
              {/* Native Messaging Info Card */}
              <div className="card" data-testid="native-messaging-info-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#8B2E3C] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#222222]">Messaging</h3>
                    <p className="text-sm text-[#666666]">The app uses your device's built-in email & texting apps — no setup needed.</p>
                  </div>
                </div>
                <ul className="text-sm text-[#444] space-y-1 mt-3 list-disc pl-5">
                  <li><strong>Email buttons</strong> open your default mail app with the recipient, subject, and message pre-filled.</li>
                  <li><strong>Text buttons</strong> open your default messaging app with the phone number (and message, when supported).</li>
                  <li><strong>Bulk emails</strong> open your mail app with everyone pre-filled as BCC so addresses stay private.</li>
                </ul>
                <p className="text-xs text-[#888] mt-3">SendGrid and Twilio integrations are available under <em>Advanced / Future Integrations</em> below and are disabled by default.</p>
              </div>

              {/* Message Templates Card */}
              <div className="card" data-testid="message-templates-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#C9A227] rounded-full flex items-center justify-center">
                    <Send className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#222222]">"Send as me" Message Templates</h3>
                    <p className="text-sm text-[#666666]">Used by the <em>Remind them</em> / <em>Thank them</em> / <em>Follow up</em> buttons inside View-as-Participant mode.</p>
                  </div>
                </div>
                <div className="text-xs text-[#666] bg-[#FBF4EA] border border-[#E9DDCF] rounded p-2 mb-4">
                  <strong>Placeholders:</strong> <code>{'{name}'}</code>, <code>{'{full_name}'}</code>, <code>{'{title}'}</code>, <code>{'{date}'}</code>, <code>{'{time}'}</code>, <code>{'{time_suffix}'}</code>, <code>{'{location}'}</code>, <code>{'{hours}'}</code>. Leave any field blank to use the built-in default.
                </div>

                {[
                  { key: 'reminder', label: 'Reminder (upcoming sign-ups)' },
                  { key: 'thankyou', label: 'Thank-you (attended)' },
                  { key: 'followup', label: 'Follow-up (missed / no-show)' },
                ].map(({ key, label }) => (
                  <div key={key} className="mb-5 pb-5 border-b border-[#E9DDCF] last:border-b-0 last:mb-0 last:pb-0">
                    <div className="text-sm font-semibold text-[#222] mb-2">{label}</div>
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-[#666] mb-1">Email subject</label>
                      <input
                        type="text"
                        className="input-field"
                        value={globalSettings[`${key}_subject`] || ''}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, [`${key}_subject`]: e.target.value }))}
                        placeholder="(uses default)"
                        data-testid={`template-${key}-subject`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#666] mb-1">Message body (used for both email & SMS)</label>
                      <textarea
                        className="input-field"
                        rows={4}
                        value={globalSettings[`${key}_template`] || ''}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, [`${key}_template`]: e.target.value }))}
                        placeholder="(uses default)"
                        data-testid={`template-${key}-body`}
                      />
                    </div>
                  </div>
                ))}

                <div className="flex justify-end">
                  <button onClick={handleSaveSettings} className="btn-primary" data-testid="save-templates-button">
                    Save Message Templates
                  </button>
                </div>
              </div>

              {/* Advanced / Future Integrations (SendGrid + Twilio) */}
              <div className="card" data-testid="advanced-integrations-card">
                <button
                  type="button"
                  onClick={() => setShowAdvancedIntegrations(v => !v)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="toggle-advanced-integrations"
                >
                  <div>
                    <h3 className="text-lg font-bold text-[#222222]">Advanced / Future Integrations</h3>
                    <p className="text-sm text-[#666666]">SendGrid (transactional email) and Twilio (SMS). Disabled by default — configure later if you want automated sending.</p>
                  </div>
                  <span className="text-[#8B2E3C] text-sm font-medium ml-4 shrink-0">
                    {showAdvancedIntegrations ? 'Hide' : 'Show'}
                  </span>
                </button>
              </div>

              {showAdvancedIntegrations && (
              <>
              {/* Email Notification Settings Card */}
              <div className="card" data-testid="email-settings-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#8B2E3C] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#222222]">Email Notifications</h3>
                    <p className="text-sm text-[#666666]">Configure SendGrid for transactional emails</p>
                  </div>
                </div>
                
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#FBF4EA] rounded-lg mb-4">
                  <div>
                    <div className="font-medium text-[#222222]">Enable Email Notifications</div>
                    <div className="text-sm text-[#666666]">Send emails for signups, approvals, and reminders</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={globalSettings.enable_email_notifications}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, enable_email_notifications: e.target.checked }))}
                      data-testid="enable-email-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-[#8B2E3C] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8B2E3C]"></div>
                  </label>
                </div>
                
                {/* SendGrid Status Badge */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-[#666666]">SendGrid Status:</span>
                  {globalSettings.sendgrid_configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3" />
                      Configured
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertTriangle className="w-3 h-3" />
                      Not configured
                    </span>
                  )}
                </div>
                
                <div className="space-y-4">
                  {/* SendGrid API Key */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Key className="w-4 h-4 text-[#8B2E3C]" />
                      SendGrid API Key
                    </label>
                    <input
                      type="password"
                      className="input-field"
                      placeholder={globalSettings.sendgrid_configured ? '••••••••••••••••' : 'Enter SendGrid API Key'}
                      value={globalSettings.sendgrid_api_key}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                      data-testid="sendgrid-api-key-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">
                      Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-[#8B2E3C] hover:underline">SendGrid Dashboard</a>
                    </p>
                  </div>
                  
                  {/* From Email */}
                  <div>
                    <label className="block text-sm font-medium mb-2">From Email</label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder="noreply@communitykindness.org"
                      value={globalSettings.from_email || ''}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, from_email: e.target.value }))}
                      data-testid="from-email-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">Must be verified in your SendGrid account</p>
                  </div>
                  
                  {/* Reply-To Email */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Reply-To Email (optional)</label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder="coordinator@communitykindness.org"
                      value={globalSettings.reply_to_email || ''}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, reply_to_email: e.target.value }))}
                      data-testid="reply-to-email-input"
                    />
                  </div>
                  
                  {/* App Base URL */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#8B2E3C]" />
                      App Base URL
                    </label>
                    <input
                      type="url"
                      className="input-field"
                      placeholder="https://yourapp.com"
                      value={globalSettings.app_base_url || ''}
                      onChange={(e) => setGlobalSettings(prev => ({ ...prev, app_base_url: e.target.value }))}
                      data-testid="app-base-url-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">Used for deep links in emails (e.g., View Opportunity)</p>
                  </div>
                  
                  {/* Reminder Timing */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#8B2E3C]" />
                      Reminder Timing
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        className="input-field w-24"
                        min="1"
                        max="72"
                        value={globalSettings.reminder_hours_before || 24}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, reminder_hours_before: parseInt(e.target.value) || 24 }))}
                        data-testid="reminder-hours-input"
                      />
                      <span className="text-sm text-[#666666]">hours before shift</span>
                    </div>
                  </div>
                </div>
                
                {/* Email Types Info */}
                <div className="mt-6 pt-4 border-t border-[#E9DDCF]">
                  <h4 className="font-medium text-[#222222] mb-3">Email Types</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <span className="font-medium">Signup Notification</span>
                        <span className="text-[#666666]"> — Sent to partner when a volunteer signs up</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <span className="font-medium">Approval Confirmation</span>
                        <span className="text-[#666666]"> — Sent to volunteer when approved</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <span className="font-medium">Shift Reminder</span>
                        <span className="text-[#666666]"> — Sent to volunteer before their shift</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                  <button
                    onClick={handleSaveSettings}
                    className="btn-primary"
                    data-testid="save-email-settings-button"
                  >
                    Save Email Settings
                  </button>
                </div>
              </div>

              {/* Twilio SMS Configuration */}
              <div className="card">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-5 h-5 text-[#8B2E3C]" />
                  <h3 className="text-lg font-bold text-[#222222]">SMS (Twilio) — optional</h3>
                  {globalSettings.twilio_configured && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Configured</span>
                  )}
                </div>
                <p className="text-sm text-[#666666] mb-4">
                  When configured, the "Text" buttons on participant pages send SMS via Twilio. Without it, those buttons open the device's native Messages app.
                </p>
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Twilio Account SID</label>
                    <input
                      type="text"
                      className="input-field"
                      value={globalSettings.twilio_account_sid || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, twilio_account_sid: e.target.value })}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      data-testid="twilio-sid-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Twilio Auth Token</label>
                    <input
                      type="password"
                      className="input-field"
                      value={globalSettings.twilio_auth_token || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, twilio_auth_token: e.target.value })}
                      placeholder={globalSettings.twilio_configured ? '••••••••••••••••  (leave blank to keep)' : 'your 32-character token'}
                      data-testid="twilio-token-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Twilio "From" number (E.164 like +15551234567)</label>
                    <input
                      type="text"
                      className="input-field"
                      value={globalSettings.twilio_from_number || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, twilio_from_number: e.target.value })}
                      placeholder="+15551234567"
                      data-testid="twilio-from-input"
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                  <button onClick={handleSaveSettings} className="btn-primary" data-testid="save-twilio-settings-button">
                    Save SMS Settings
                  </button>
                </div>
              </div>
              </>
              )}
              
              {/* Preview Card */}
              <div className="card">
                <h3 className="text-lg font-bold text-[#222222] mb-4">Preview</h3>
                <p className="text-sm text-[#666666] mb-4">
                  This is how the donation card will appear to partners:
                </p>
                <div className="bg-[#FBF4EA] rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#8B2E3C] rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♥</span>
                    </div>
                    <div>
                      <div className="font-bold text-[#222222]">Support this tool</div>
                      <div className="text-sm text-[#666666]">Help keep Community Kindness Circle running</div>
                    </div>
                  </div>
                  <div className="space-y-2 mb-3">
                    {(globalSettings.suggested_amounts || []).filter(a => a.label && a.amount).map((item, index) => (
                      <div key={`preview-${item.label}-${item.amount}`} className="flex justify-between text-sm bg-white rounded-lg px-3 py-2">
                        <span className="text-[#666666]">{item.label}</span>
                        <span className="font-medium text-[#8B2E3C]">{item.amount}</span>
                      </div>
                    ))}
                    {(!globalSettings.suggested_amounts || globalSettings.suggested_amounts.filter(a => a.label && a.amount).length === 0) && (
                      <div className="text-sm text-[#666666] italic">No suggested amounts configured</div>
                    )}
                  </div>
                  {globalSettings.donation_url ? (
                    <button className="btn-primary w-full">Support the tool</button>
                  ) : (
                    <div className="text-center py-2 bg-white rounded-lg text-sm text-[#666666] italic">
                      Donation link coming soon
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 3: Edit History Modal */}
      {historyOpp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="history-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E9DDCF] flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-2xl font-bold text-[#222222]">Edit History</h2>
                <p className="text-sm text-[#666] mt-1">{historyOpp.title}</p>
              </div>
              <button
                onClick={() => { setHistoryOpp(null); setHistoryEntries([]); }}
                className="text-[#666] hover:text-[#222]"
                data-testid="close-history-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {historyLoading ? (
                <p className="text-center text-[#666] py-8">Loading...</p>
              ) : historyEntries.length === 0 ? (
                <p className="text-center text-[#666] py-8" data-testid="history-empty">No edits yet. Future edits will be tracked here.</p>
              ) : (
                <ol className="space-y-4" data-testid="history-list">
                  {historyEntries.map((entry, i) => (
                    <li key={entry.id || i} className="border-l-2 border-[#8B2E3C] pl-4 pb-2">
                      <div className="text-sm text-[#222] font-semibold">
                        {entry.changed_by_name || entry.changed_by_email || 'Unknown user'}
                        <span className="text-[#666] font-normal ml-2 text-xs">
                          ({entry.changed_by_role || 'user'}) • {entry.changed_at ? format(new Date(entry.changed_at), 'MMM d, yyyy h:mm a') : ''}
                        </span>
                      </div>
                      <ul className="mt-2 space-y-1">
                        {(entry.changes || []).map((c, j) => (
                          <li key={j} className="text-xs text-[#444] bg-[#FBF4EA] rounded px-2 py-1">
                            <span className="font-medium text-[#8B2E3C]">{c.field}</span>:
                            <span className="line-through text-[#999] ml-1">{c.old === null || c.old === undefined ? 'empty' : String(c.old).slice(0, 80)}</span>
                            <span className="mx-1">→</span>
                            <span className="text-[#222]">{c.new === null || c.new === undefined ? 'empty' : String(c.new).slice(0, 80)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Hours Modal */}
      <ManualHoursModal
        isOpen={showManualHours}
        onClose={() => setShowManualHours(false)}
        onSaved={() => { fetchData(); fetchStats && fetchStats(); }}
        scope="admin"
      />

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmail}
        onClose={() => setShowBulkEmail(false)}
        scope="admin"
      />
    </div>
  );
};

export default AdminDashboard;